import React, { useEffect, useState } from "react";

/* Config */

/* Components */
import {
  Banner,
  Container,
  LoadingScreen,
  PaginationControls,
} from "../../utils/components/Common";
import { Table, FilterDropdown } from "../reports/Reports.table.elements";
import { ICellProps } from "../reports/Reports.table.elements";

/* APIs - Hooks - Utils */
import { User } from "../user/userApi";
import { useListReportsQuery } from "../reports/reportApi";
import { toastQueryError } from "../../utils/toasts";

type ReportListPanelProps = {
  user: User;
  isLoggedIn: boolean;
};

const ReportListPanel = ({
  user,
  isLoggedIn,
}: ReportListPanelProps): JSX.Element => {
  const [processing, setProcessing] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageItems] = useState(10);
  const {
    data: reports,
    error,
    isFetching,
    isError,
    isLoading,
    refetch,
  } = useListReportsQuery({ page: page, page_size: pageSize });

  useEffect(() => {
    if (error) {
      toastQueryError(error);
    }
  }, [isError]);

  const onPageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageItems(parseInt(e.target.value));
    setPage(1);
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Report ID",
        accessor: "id",
      },
      {
        Header: "Monitor",
        accessor: "monitor",
        // Custom cell to map monitor value to config temp monitor
      },
      {
        Header: "URL",
        accessor: "url",
        Filter: FilterDropdown,
        disableSortBy: true,
        Cell: ({ value }: ICellProps) => (
          <a href={value} target="_blank" rel="noreferrer">
            {value}
          </a>
        ),
      },
      {
        Header: "Status Code",
        accessor: "status_code",
        Filter: FilterDropdown,
        disableSortBy: true,
      },
      {
        Header: "Is OK",
        accessor: "is_ok",
        Filter: FilterDropdown,
        disableSortBy: true,
        Cell: ({ value }: ICellProps) => <span>{`${value}`}</span>,
      },
      { Header: "Elapsed Total Seconds", accessor: "elapsed_total_seconds" },
      {
        Header: "Request Time",
        accessor: "request_time",
        /* Convert request_time to locale time */
        Cell: ({ value }: ICellProps) => (
          <span>{new Date(value).toLocaleString()}</span>
        ),
      },
      {
        Header: "Reason",
        accessor: "reason",
        Filter: FilterDropdown,
        disableSortBy: true,
        Cell: ({ value }: ICellProps) => <span>{`${value}`}</span>,
      },
      {
        Header: "Is Redirect",
        accessor: "is_redirect",
        Cell: ({ value }: ICellProps) => <span>{`${value}`}</span>,
      },
    ],
    []
  );

  useEffect(() => {
    if (isFetching || isLoading) {
      setProcessing(true);
    } else {
      setProcessing(false);
    }
    if (isError && error) {
      toastQueryError(error);
    }
  }, [isLoading, isFetching]);

  return (
    <>
      {user && isLoggedIn ? (
        <Container padding="py-6">
          {processing ? <LoadingScreen /> : null}
          <PaginationControls
            paginatedResponse={reports}
            setPage={setPage}
            refetch={refetch}
            onPageSizeChange={onPageSizeChange}
          />
          {reports ? <Table columns={columns} data={reports.data} /> : null}
        </Container>
      ) : (
        <Banner
          heading="Protected Route"
          message="This is a protected route. Authentication required."
        />
      )}
    </>
  );
};

export default ReportListPanel;
